import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FactCheck as FactCheckIcon,
  PendingActions as PendingActionsIcon,
  ReportProblemOutlined as ReportProblemIcon,
  Block as BlockIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaContabilidad,
} from 'lib/seguridad/permisosPorModulo';
import { ConfirmarDialog, FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import {
  formatearFecha,
  fechaLocalHoy,
  cardShellSx,
  filterHeaderSx,
  cellHeadSx,
  btnPrimarySx,
  btnOutlinedSx,
} from './entradaDiarioShared';
import {
  MESES,
  PROGRAMA_PASE_FISCAL,
  SEMAFORO,
  claveAsiento,
  fmtMoney,
  mesFiscalCerrado,
  resumenSeleccion,
  seleccionInicial,
  sePuedePasar,
} from './paseFiscalShared';
import SoloNoFiscal from './SoloNoFiscal';
import { useVersionContable } from './versionContable';

const COLOR_SEMAFORO = {
  listo: { bg: FP.color.successSoft, fg: FP.color.successDark },
  sin_comprobante: { bg: '#FFF4E0', fg: '#9A6200' },
  bloqueado: { bg: FP.color.dangerSoft, fg: FP.color.danger },
};

// Fila de encabezado dentro del TableBody, como el resto del módulo: el tema
// (assets/theme/components/table/tableHead) rompe el layout de <thead>.
// cellHeadSx está hecho para un <span> (display: block); en la celda misma
// la sacaría del layout de tabla y las columnas se apilarían.
const celdaEncabezadoSx = { ...cellHeadSx, display: 'table-cell', mb: 0 };

const filaEncabezadoSx = {
  '& td': {
    border: 'none',
    borderBottom: `2px solid ${FP.color.border}`,
    bgcolor: FP.color.surfaceAlt,
    whiteSpace: 'nowrap',
  },
};

function PaseFiscalContenido() {
  const { controller, showError, showSuccess, showWarning } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';
  const { activa, cargado, refrescar: refrescarVersion } = useVersionContable();

  const puedeLeer =
    tienePermisosModulo(roleDetalles, PROGRAMA_PASE_FISCAL) ||
    puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_PASE_FISCAL);
  const puedeModificar = puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_PASE_FISCAL);

  const [tab, setTab] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [fechaHasta, setFechaHasta] = useState(fechaLocalHoy);
  const [documento, setDocumento] = useState('');
  const [documentos, setDocumentos] = useState([]);
  const [asientos, setAsientos] = useState([]);
  const [truncado, setTruncado] = useState(false);
  const [maxPorPase, setMaxPorPase] = useState(500);
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [periodo, setPeriodo] = useState({ ano: '', mes: '' });
  const [lotes, setLotes] = useState([]);
  const [confirmarPase, setConfirmarPase] = useState(false);
  const [loteAReversar, setLoteAReversar] = useState(null);
  const [detalle, setDetalle] = useState(null); // { asiento, cargando, lineas, debito, credito, diferencia }

  const llamar = useCallback(
    async (ruta, cuerpo = {}) => {
      const response = await fetch(`${servidor}/contabilidadbackend_pasefiscal_${ruta}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuario: usuarioLog || '', password: claveLog || '', version: 'N', ...cuerpo }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(data.Error || 'Error en el pase a fiscal');
        error.data = data;
        throw error;
      }
      return data;
    },
    [servidor, usuarioLog, claveLog]
  );

  const cargarPendientes = useCallback(async () => {
    setCargando(true);
    try {
      const data = await llamar('pendientes', {
        fecha_hasta: fechaHasta || undefined,
        documento: documento || undefined,
      });
      const lista = data.asientos || [];
      setAsientos(lista);
      setSeleccion(seleccionInicial(lista));
      setTruncado(Boolean(data.truncado));
      setMaxPorPase(data.max_por_pase || 500);
      if (!documento) setDocumentos(data.documentos || []);
      setPeriodo({ ano: data.anoinicialfiscal || '', mes: data.mesinicialfiscal || '' });
    } catch (e) {
      showError(e.message);
      setAsientos([]);
      setSeleccion(new Set());
    } finally {
      setCargando(false);
    }
  }, [llamar, fechaHasta, documento, showError]);

  const cargarLotes = useCallback(async () => {
    try {
      const data = await llamar('lotes');
      setLotes(data.lotes || []);
    } catch (e) {
      showError(e.message);
    }
  }, [llamar, showError]);

  useEffect(() => {
    if (!activa || !puedeLeer || !usuarioLog) return;
    cargarPendientes();
    cargarLotes();
    // Carga inicial; después se recarga con «Buscar» o tras cada operación.
  }, [activa, puedeLeer, usuarioLog]);

  const resumen = useMemo(
    () => resumenSeleccion(asientos, seleccion, periodo.ano, periodo.mes),
    [asientos, seleccion, periodo]
  );
  const conteo = useMemo(() => {
    const c = { listo: 0, sin_comprobante: 0, bloqueado: 0, excluidos: 0 };
    asientos.forEach((a) => {
      if (a.estadofiscal === 'X') c.excluidos += 1;
      else c[a.semaforo] = (c[a.semaforo] || 0) + 1;
    });
    return c;
  }, [asientos]);

  const alternar = (a) => {
    const k = claveAsiento(a);
    setSeleccion((prev) => {
      const s = new Set(prev);
      if (s.has(k)) s.delete(k);
      else s.add(k);
      return s;
    });
  };
  const pasables = useMemo(() => asientos.filter(sePuedePasar), [asientos]);
  const todosMarcados = pasables.length > 0 && pasables.every((a) => seleccion.has(claveAsiento(a)));
  const alternarTodos = () =>
    setSeleccion(todosMarcados ? new Set() : new Set(pasables.map(claveAsiento)));

  const tras = async (mensaje) => {
    showSuccess(mensaje);
    await Promise.all([cargarPendientes(), cargarLotes()]);
    refrescarVersion();
  };

  const pasar = async () => {
    setProcesando(true);
    try {
      const data = await llamar('pasar', {
        asientos: resumen.asientos.map((a) => ({ documento: a.documento, numero: a.numero })),
      });
      setConfirmarPase(false);
      if ((data.rechazados || []).length) {
        showWarning(`${data.rechazados.length} asiento(s) no pasaron: ${data.rechazados[0].motivo}`);
      }
      await tras(`Lote ${data.lote}: ${data.pasados} asiento(s) pasados a fiscal`);
    } catch (e) {
      showError(e.message);
    } finally {
      setProcesando(false);
    }
  };

  const excluir = async (a, excluirlo) => {
    setProcesando(true);
    try {
      await llamar('excluir', {
        asientos: [{ documento: a.documento, numero: a.numero }],
        excluir: excluirlo,
      });
      await tras(excluirlo ? 'Asiento excluido del pase' : 'Asiento devuelto a pendientes');
    } catch (e) {
      showError(e.message);
    } finally {
      setProcesando(false);
    }
  };

  const reversar = async () => {
    setProcesando(true);
    try {
      await llamar('reversar', { lote: loteAReversar.lote });
      setLoteAReversar(null);
      await tras(`Lote ${loteAReversar.lote} reversado: sus asientos vuelven a pendientes`);
    } catch (e) {
      showError(e.message);
    } finally {
      setProcesando(false);
    }
  };

  const verLineas = async (a) => {
    setDetalle({ asiento: a, cargando: true, lineas: [] });
    try {
      const data = await llamar('lineas', { documento: a.documento, numero: a.numero });
      setDetalle({ asiento: a, cargando: false, ...data });
    } catch (e) {
      showError(e.message);
      setDetalle(null);
    }
  };

  const guardarPeriodo = async () => {
    setProcesando(true);
    try {
      await llamar('guardarperiodo', { ano: periodo.ano, mes: periodo.mes });
      showSuccess('Período fiscal guardado');
      refrescarVersion();
    } catch (e) {
      showError(e.message);
    } finally {
      setProcesando(false);
    }
  };

  const header = (
    <PageHeader
      title="Pase a fiscal"
      subtitle={[nombreNegocio, 'Asientos pendientes de la versión no fiscal'].filter(Boolean).join(' · ')}
      cta={
        <Button
          variant="contained"
          startIcon={<FactCheckIcon />}
          disabled={!puedeModificar || procesando || resumen.cantidad === 0}
          onClick={() => setConfirmarPase(true)}
          sx={{ ...btnPrimarySx, height: 42 }}
        >
          Pasar a fiscal ({resumen.cantidad})
        </Button>
      }
    />
  );

  if (!puedeLeer || (cargado && !activa)) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Pase a fiscal" subtitle={nombreNegocio || ''} />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Alert severity="info">
            {!puedeLeer
              ? 'No tiene permisos para esta pantalla.'
              : 'Este negocio no usa las versiones fiscal / no fiscal (Configuración → Parámetros).'}
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      {header}
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={6} md={3}>
            <StatCard label="Listos" value={String(conteo.listo)} icon={FactCheckIcon} accent={FP.color.successDark} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Sin comprobante" value={String(conteo.sin_comprobante)} icon={ReportProblemIcon} accent="#9A6200" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Bloqueados" value={String(conteo.bloqueado)} icon={BlockIcon} accent={FP.color.danger} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Seleccionado" value={`$${fmtMoney(resumen.debito)}`} icon={PendingActionsIcon} accent={FP.color.navy} />
          </Grid>
        </Grid>

        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={`Pendientes (${asientos.length})`} />
          <Tab label={`Historial de pases (${lotes.length})`} />
          <Tab label="Período fiscal" />
        </Tabs>

        {tab === 0 && (
          <Card elevation={0} sx={cardShellSx}>
            <Box sx={{ ...filterHeaderSx, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Hasta la fecha"
                type="date"
                size="small"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ ...fpInputSx, width: 180 }}
              />
              <TextField
                select
                label="Documento"
                size="small"
                value={documento}
                onChange={(e) => setDocumento(e.target.value)}
                sx={{ ...fpInputSx, minWidth: 220 }}
              >
                <MenuItem value="">Todos</MenuItem>
                {documentos.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="outlined" onClick={cargarPendientes} disabled={cargando} sx={btnOutlinedSx}>
                Buscar
              </Button>
              {cargando && <CircularProgress size={20} />}
            </Box>

            {truncado && (
              <Alert severity="warning" sx={{ m: 2 }}>
                Hay más asientos de los que se muestran. Acote por fecha o documento y pase por partes.
              </Alert>
            )}
            {resumen.cantidad > maxPorPase && (
              <Alert severity="warning" sx={{ m: 2 }}>
                Máximo {maxPorPase} asientos por pase; tiene {resumen.cantidad} seleccionados.
              </Alert>
            )}

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1100 }}>
                <TableBody>
                  <TableRow sx={filaEncabezadoSx}>
                    <TableCell padding="checkbox" sx={celdaEncabezadoSx}>
                      <Checkbox
                        size="small"
                        checked={todosMarcados}
                        indeterminate={!todosMarcados && resumen.cantidad > 0}
                        onChange={alternarTodos}
                        disabled={!puedeModificar || pasables.length === 0}
                      />
                    </TableCell>
                    <TableCell sx={celdaEncabezadoSx}>Documento</TableCell>
                    <TableCell sx={celdaEncabezadoSx}>Nº</TableCell>
                    <TableCell sx={celdaEncabezadoSx} align="center">Fecha</TableCell>
                    <TableCell sx={celdaEncabezadoSx}>Concepto</TableCell>
                    <TableCell sx={celdaEncabezadoSx} align="right">Débito</TableCell>
                    <TableCell sx={celdaEncabezadoSx} align="right">Crédito</TableCell>
                    <TableCell sx={celdaEncabezadoSx}>NCF</TableCell>
                    <TableCell sx={celdaEncabezadoSx} align="center">Estado</TableCell>
                    <TableCell sx={celdaEncabezadoSx} align="center" />
                  </TableRow>
                  {asientos.length === 0 && !cargando && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 5, color: FP.color.textMuted }}>
                        No hay asientos pendientes de pasar a fiscal.
                      </TableCell>
                    </TableRow>
                  )}
                  {asientos.map((a) => {
                    const excluido = a.estadofiscal === 'X';
                    const color = COLOR_SEMAFORO[a.semaforo] || COLOR_SEMAFORO.listo;
                    const cerrado = mesFiscalCerrado(a.fecha, periodo.ano, periodo.mes);
                    return (
                      <TableRow key={claveAsiento(a)} hover sx={excluido ? { opacity: 0.55 } : undefined}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={seleccion.has(claveAsiento(a))}
                            onChange={() => alternar(a)}
                            disabled={!puedeModificar || !sePuedePasar(a)}
                          />
                        </TableCell>
                        <TableCell>{a.documento}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{a.numero}</TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          {formatearFecha(a.fecha)}
                          {cerrado && (
                            <Tooltip title="Mes fiscal cerrado: entra a fiscal con la fecha del pase">
                              <Typography component="span" sx={{ ml: 0.5, color: '#9A6200', fontWeight: 700 }}>
                                *
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.concepto}>
                          {a.concepto || '—'}
                        </TableCell>
                        <TableCell align="right">${fmtMoney(a.debito)}</TableCell>
                        <TableCell align="right">${fmtMoney(a.credito)}</TableCell>
                        <TableCell>{a.ncf || '—'}</TableCell>
                        <TableCell align="center">
                          <Tooltip title={excluido ? 'Excluido del pase' : a.motivo || ''}>
                            <Chip
                              size="small"
                              label={excluido ? 'Excluido' : SEMAFORO[a.semaforo]?.etiqueta || a.semaforo}
                              sx={{
                                fontWeight: 700,
                                bgcolor: excluido ? FP.color.surfaceAlt : color.bg,
                                color: excluido ? FP.color.textMuted : color.fg,
                              }}
                            />
                          </Tooltip>
                          {!excluido && a.motivo && (
                            <Typography sx={{ fontSize: '0.7rem', color: FP.color.textMuted, mt: 0.3 }}>
                              {a.semaforo === 'bloqueado' && a.motivo === 'Asiento descuadrado'
                                ? `Descuadre de $${fmtMoney(Math.abs(a.debito - a.credito))}`
                                : a.motivo}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Ver líneas del asiento">
                            <IconButton size="small" onClick={() => verLineas(a)} sx={{ color: FP.color.navy }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Button
                            size="small"
                            disabled={!puedeModificar || procesando}
                            onClick={() => excluir(a, !excluido)}
                            sx={{ textTransform: 'none' }}
                          >
                            {excluido ? 'Restaurar' : 'Excluir'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {tab === 1 && (
          <Card elevation={0} sx={cardShellSx}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1100 }}>
                <TableBody>
                  <TableRow sx={filaEncabezadoSx}>
                    <TableCell sx={celdaEncabezadoSx}>Lote</TableCell>
                    <TableCell sx={celdaEncabezadoSx}>Fecha</TableCell>
                    <TableCell sx={celdaEncabezadoSx}>Usuario</TableCell>
                    <TableCell sx={celdaEncabezadoSx} align="right">Asientos</TableCell>
                    <TableCell sx={celdaEncabezadoSx} align="right">Débito</TableCell>
                    <TableCell sx={celdaEncabezadoSx} align="right">Crédito</TableCell>
                    <TableCell sx={celdaEncabezadoSx} align="center">Estado</TableCell>
                    <TableCell sx={celdaEncabezadoSx} />
                  </TableRow>
                  {lotes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5, color: FP.color.textMuted }}>
                        Todavía no se ha hecho ningún pase.
                      </TableCell>
                    </TableRow>
                  )}
                  {lotes.map((l) => (
                    <TableRow key={l.lote} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{l.lote}</TableCell>
                      <TableCell>{l.fecha}</TableCell>
                      <TableCell>{l.usuario}</TableCell>
                      <TableCell align="right">{l.asientos}</TableCell>
                      <TableCell align="right">${fmtMoney(l.debito)}</TableCell>
                      <TableCell align="right">${fmtMoney(l.credito)}</TableCell>
                      <TableCell align="center">
                        {l.estado === 'R' ? (
                          <Tooltip title={`Por ${l.reversadopor} el ${l.fechareverso}`}>
                            <Chip size="small" label="Reversado" />
                          </Tooltip>
                        ) : (
                          <Chip
                            size="small"
                            label="Activo"
                            sx={{ fontWeight: 700, bgcolor: FP.color.successSoft, color: FP.color.successDark }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {l.estado !== 'R' && (
                          <Button
                            size="small"
                            color="error"
                            disabled={!puedeModificar || procesando}
                            onClick={() => setLoteAReversar(l)}
                            sx={{ textTransform: 'none' }}
                          >
                            Reversar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {tab === 2 && (
          <Card elevation={0} sx={{ ...cardShellSx, p: 2.5, maxWidth: 640 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Primer mes fiscal abierto</Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem', mb: 2 }}>
              Los meses anteriores quedan cerrados en la versión fiscal (ya declarados). Un asiento con
              fecha de un mes cerrado entra a fiscal con la fecha del pase, y un lote con asientos en un
              mes cerrado ya no se puede reversar. La versión no fiscal conserva siempre la fecha real.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                select
                label="Mes"
                size="small"
                value={periodo.mes}
                onChange={(e) => setPeriodo((p) => ({ ...p, mes: e.target.value }))}
                sx={{ ...fpInputSx, width: 180 }}
              >
                {MESES.map((m, i) => (
                  <MenuItem key={m} value={i + 1}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Año"
                type="number"
                size="small"
                value={periodo.ano}
                onChange={(e) => setPeriodo((p) => ({ ...p, ano: e.target.value }))}
                sx={{ ...fpInputSx, width: 120 }}
              />
              <Button
                variant="contained"
                disabled={!puedeModificar || procesando || !periodo.ano || !periodo.mes}
                onClick={guardarPeriodo}
                sx={btnPrimarySx}
              >
                Guardar
              </Button>
            </Box>
          </Card>
        )}
      </Box>

      <ConfirmarDialog
        abierto={confirmarPase}
        titulo="Pasar a fiscal"
        textoConfirmar={`Pasar ${resumen.cantidad} asiento(s)`}
        confirmarDeshabilitado={resumen.cantidad === 0 || resumen.cantidad > maxPorPase}
        cargando={procesando}
        onCerrar={() => setConfirmarPase(false)}
        onConfirmar={pasar}
      >
        <Typography sx={{ mb: 1.5 }}>
          Débito ${fmtMoney(resumen.debito)} · Crédito ${fmtMoney(resumen.credito)}
        </Typography>
        <Table size="small" sx={{ mb: 1.5 }}>
          <TableBody>
            {resumen.porDocumento.map((d) => (
              <TableRow key={d.documento}>
                <TableCell>{d.documento}</TableCell>
                <TableCell align="right">{d.asientos} asiento(s)</TableCell>
                <TableCell align="right">${fmtMoney(d.debito)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {resumen.sinComprobante > 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {resumen.sinComprobante} asiento(s) sin comprobante: quedarán en los libros fiscales pero no
            saldrán en el 606 / 607.
          </Alert>
        )}
        {resumen.conFechaDelPase > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {resumen.conFechaDelPase} asiento(s) son de un mes fiscal cerrado: entran con la fecha de hoy.
          </Alert>
        )}
        <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
          El pase queda como un lote que se puede reversar mientras su mes fiscal siga abierto.
        </Typography>
      </ConfirmarDialog>

      <Dialog open={Boolean(detalle)} onClose={() => setDetalle(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {detalle?.asiento?.documento} · Nº {detalle?.asiento?.numero}
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
            {formatearFecha(detalle?.asiento?.fecha)} · {detalle?.asiento?.concepto || '—'}
            {detalle?.asiento?.ncf ? ` · ${detalle.asiento.ncf}` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {detalle?.cargando ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableBody>
                  <TableRow sx={filaEncabezadoSx}>
                    <TableCell sx={celdaEncabezadoSx}>Cuenta</TableCell>
                    <TableCell sx={celdaEncabezadoSx}>Nombre</TableCell>
                    <TableCell sx={celdaEncabezadoSx}>Centro</TableCell>
                    <TableCell sx={celdaEncabezadoSx} align="right">Débito</TableCell>
                    <TableCell sx={celdaEncabezadoSx} align="right">Crédito</TableCell>
                  </TableRow>
                  {(detalle?.lineas || []).map((ln, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableRow key={`${ln.cuenta}-${i}`}>
                      <TableCell sx={{ fontWeight: 700 }}>{ln.cuenta || '—'}</TableCell>
                      <TableCell sx={ln.sin_catalogo ? { color: FP.color.danger } : undefined}>
                        {ln.sin_catalogo ? 'No existe en el catálogo' : ln.nombre || '—'}
                      </TableCell>
                      <TableCell>{ln.centro || '—'}</TableCell>
                      <TableCell align="right">{ln.debito ? `$${fmtMoney(ln.debito)}` : ''}</TableCell>
                      <TableCell align="right">{ln.credito ? `$${fmtMoney(ln.credito)}` : ''}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 800 }}>
                      Totales
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>${fmtMoney(detalle?.debito)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>${fmtMoney(detalle?.credito)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {Math.abs(detalle?.diferencia || 0) >= 0.01 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Descuadre de ${fmtMoney(Math.abs(detalle.diferencia))}: falta ese monto al{' '}
                  {detalle.diferencia > 0 ? 'crédito' : 'débito'}. Revise en Config. Documento Contable
                  que todas las reglas de este tipo de documento tengan cuenta.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetalle(null)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmarDialog
        abierto={Boolean(loteAReversar)}
        titulo={`Reversar el lote ${loteAReversar?.lote || ''}`}
        mensaje={`Sus ${loteAReversar?.asientos || 0} asiento(s) salen de la versión fiscal y vuelven a pendientes.`}
        textoConfirmar="Reversar"
        destructivo
        cargando={procesando}
        onCerrar={() => setLoteAReversar(null)}
        onConfirmar={reversar}
      />
    </Box>
  );
}

/** El pase se opera desde la versión no fiscal, donde está todo lo registrado. */
export default function PaseFiscal() {
  return (
    <SoloNoFiscal titulo="Pase a fiscal">
      <PaseFiscalContenido />
    </SoloNoFiscal>
  );
}
